<script setup generic="T" lang="ts">
// TODO провести рефакторинг
import './style.scss';

import { UiIcon, UiDropdown } from '@shared/ui';
import type { UiSelectProps, UiSelectEmits } from './types';

const className = 'ui-select';

const model = defineModel<unknown>();

const emit = defineEmits<UiSelectEmits>();

const {
  options,
  initialOptions,
  labelKey,
  valueKey,
  emptyLabel = 'Выберите из списка...',
  label,
  selectClass,
  info,
  error,
  success,
  required = false,
  maxHeight = 300,
  searchable = false,
} = defineProps<UiSelectProps<T>>();

const searchTerm = ref('');

const initOptions = ref<T[]>(initialOptions ?? options) as Ref<T[]>;

const filteredOptions = computed(() => {
  if (!searchable) return initOptions.value;
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return initOptions.value;
  return initOptions.value.filter(opt =>
    String(opt[labelKey]).toLowerCase().includes(term),
  );
});

const selectedValue = computed(
  () => initOptions.value?.find(option => option[valueKey] === model.value)?.[labelKey],
);
const showOptions = ref(false);
const selectRef = ref<HTMLElement>();
const uid = useId();

const onSelectOption = (option: T) => {
  if (initialOptions && !initOptions.value.find(opt => opt[valueKey] === option[valueKey])) {
    initOptions.value = [...initOptions.value, option];
  }
  model.value = option[valueKey as keyof T];
  showOptions.value = false;
  if (searchable) searchTerm.value = '';
  emit('onSelect', option);
};

watch(showOptions, (val) => {
  if (val && searchable) {
    nextTick(() => {
      const inp = selectRef.value?.querySelector('input') as HTMLElement;
      inp?.focus();
    });
  }
});

onClickOutside(selectRef, () => (showOptions.value = false));
</script>

<template>
  <div
    ref="selectRef"
    :class="className"
  >
    <label
      v-if="label"
      :for="uid"
    >
      {{ label }} <span
        v-if="required"
        class="required"
      >*</span>
    </label>
    <button
      :id="uid"
      type="button"
      :class="['select-box', selectClass, { errored: error }]"
      @click="showOptions = !showOptions"
    >
      <template v-if="showOptions && searchable">
        <input
          v-model="searchTerm"
          class="select-search-input"
          :placeholder="emptyLabel"
          @click.stop
        >
      </template>
      <template v-else>
        <span v-if="selectedValue">{{ selectedValue }}</span>
        <span
          v-else
          :class="`${className}__placeholder`"
        >{{ emptyLabel }}</span>
      </template>
      <UiIcon :name="showOptions ? 'chevron-up' : 'chevron-down'" />
    </button>

    <UiDropdown
      :visible="showOptions"
      :offset="label ? 70 : 45"
      position="left"
      :max-height="maxHeight"
      full-width
    >
      <ul class="params-list">
        <li
          v-for="option in filteredOptions"
          :key="String(option[valueKey])"
        >
          <button @click="onSelectOption(option)">
            {{ option[labelKey as keyof T] }}
          </button>
        </li>
        <li v-if="filteredOptions.length === 0">
          <button disabled>
            Ничего не найдено...
          </button>
        </li>
      </ul>
    </UiDropdown>

    <p
      v-if="info"
      class="message info"
    >
      {{ info }}
    </p>
    <p
      v-if="error"
      class="message error"
    >
      {{ error }}
    </p>
    <p
      v-if="success"
      class="message success"
    >
      {{ success }}
    </p>
  </div>
</template>
